import { morphedRead } from "../auth/morphed-client";
import { registerEntityEndpoints } from "../auth/entity-endpoint-registry";
import { resolveFileKeys, type FileKeyMap } from "../utils/media";

export type InputType =
	| "text" | "text-area" | "url" | "number" | "select" | "switch"
	| "datetime" | "autocomplete" | "file-upload" | "object" | "objectArray"
	| "no-input" | "system-generated"
	| "email" | "password" | "date" | "time";

export type ViewerType =
	| "text" | "link" | "badge" | "status" | "date" | "datetime"
	| "image" | "video" | "file" | "chips" | "relation" | "relationMany"
	| "objectSummary" | "arrayCount" | "code" | "mono"
	| "avatar" | "progressBar" | "rating";

export interface MergedAttribute {
	attributeKey: string;
	attributeName: string;
	attributeDescription: string;
	morphedAttributeType: string;
	morphedVariableType: string;
	morphedInputType: string;
	morphedConstraints: string[];
	morphedConstraintOptions: Record<string, unknown>[];
	morphedInputTypeOptions: Record<string, unknown>[];
	attributeRelation: string | null;
	attributeAttributes: MergedAttribute[];
	inputType: InputType;
	viewerType: ViewerType;
	effectiveInputType: InputType;
	effectiveOutputType: ViewerType;
	isRequired: boolean;
	isEditable: boolean;
	isCreateable: boolean;
	isDisplayable: boolean;
	relatedWith: string | null;
	placeholder: string;
	selectOptions: string[];
	columnWidth: number;
	isSortable: boolean;
	isVisibleByDefault: boolean;
	isHidden: boolean;
	label?: string;
	formatOptions?: Record<string, unknown>;
	fileKeys?: FileKeyMap;
}

export interface EntityDefinition {
	id: string;
	entityName: string;
	entityType: string;
	entityDescription: string;
	entityVersion: string;
	entityEndpoint: string;
	entityStorageName: string;
	entitySchema: string;
	entityDatabase: string;
	public: boolean;
	mappedAttributes: MergedAttribute[];
	entityAttributes: EntityAttribute[];
}

export interface EntityAttribute {
	attributeKey: string;
	attributeName: string;
	attributeDescription: string;
	morphedAttributeType: string;
	morphedVariableType: string;
	morphedInputType: string;
	morphedConstraints: string[];
	morphedConstraintOptions: Record<string, unknown>[];
	morphedInputTypeOptions: Record<string, unknown>[];
	attributeRelation: string | null;
	attributeAttributes: EntityAttribute[];
}

export interface ToolConfig {
	id: string;
	entityName: string;
	toolLabel: string;
	toolDescription: string;
	toolIcon: string;
	toolPath: string;
	toolGroup: string;
	defaultView: string;
	isActive: boolean;
	sortOrder: number;
	public?: boolean;
	entityDefaults?: Record<string, unknown>;
	displayConfig?: DisplayConfig;
	inputConfig?: InputConfigEntry[];
	outputConfig?: OutputConfigEntry[];
	objectConfig?: Record<string, unknown>[];
	filterConfig?: Record<string, unknown>[];
	sortConfig?: Record<string, unknown>[];
	roleRestrictions?: Record<string, unknown>[];
	entityRelatedData?: RelatedDataEntry[];
}

export interface CardImageConfig {
	attrKey: string;
	filterKey?: string;
	filterValue?: string;
	urlKey?: string;
}

export interface DisplayConfig {
	cardImage?: CardImageConfig | string;
	cardTitle?: string;
	cardStatus?: string;
	cardFields?: string[];
	tableColumns?: string[];
	tableMaxColumns?: number;
	accordionImage?: CardImageConfig | string;
	accordionTitle?: string;
	accordionStatus?: string;
	accordionFields?: string[];
}

export interface InputConfigEntry {
	attributeKey: string;
	inputTypeOverride?: string;
	selectOptions?: string[];
	placeholder?: string;
	label?: string;
	isRequired?: boolean;
	isEditable?: boolean;
	isHidden?: boolean;
}

export interface OutputConfigEntry {
	attributeKey: string;
	outputType?: string;
	formatOptions?: Record<string, unknown>;
	columnWidth?: number;
	isSortable?: boolean;
	isVisibleByDefault?: boolean;
}

export interface RelatedDataEntry {
	entityName: string;
	displayKey: string;
	secondaryKeys: string[];
}

function detectAccept(key: string): string {
	const k = key.toLowerCase();
	if (/(video|mp4)/.test(k)) return "video/*";
	if (/(image|img|logo|hero|photo|avatar|thumbnail|banner)/.test(k)) return "image/*";
	return "";
}

const PURE_FILE_KEYS = new Set(["url", "key", "filename", "size", "mimeType"]);

function isPureFileShape(children: EntityAttribute[] | undefined, keyMap?: FileKeyMap): boolean {
	if (!children || children.length === 0) return true;
	const fileKeySet = keyMap ? new Set(Object.values(keyMap)) : PURE_FILE_KEYS;
	return children.every((c) => fileKeySet.has(c.attributeKey));
}

export function mapAttribute(a: EntityAttribute): MergedAttribute {
	const attrType = a.morphedAttributeType;
	const varType = a.morphedVariableType;
	const inputRaw = a.morphedInputType;
	const constraints = a.morphedConstraints || [];
	const opts = a.morphedConstraintOptions || [];

	let inputType: InputType = "text";
	let viewerType: ViewerType = "text";
	let isDisplayable = true;
	let fileKeys: FileKeyMap | undefined;

	if (attrType === "system") {
		inputType = "system-generated";
		viewerType = "text";
		isDisplayable = a.attributeKey === "id";
	} else if (attrType === "basic-computed") {
		inputType = "no-input";
		viewerType = "text";
	} else if (inputRaw === "file-upload") {
		fileKeys = resolveFileKeys(a);
		const pureShape = isPureFileShape(a.attributeAttributes, fileKeys);
		if (!pureShape) {
			inputType = varType === "objectArray" ? "objectArray" : "object";
			viewerType = varType === "objectArray" ? "arrayCount" : "objectSummary";
		} else {
			inputType = "file-upload";
			viewerType = "image";
		}
	} else if (attrType === "relation" || varType === "id" || varType === "idArray") {
		inputType = "autocomplete";
		viewerType = varType === "idArray" ? "relationMany" : "relation";
	} else if (attrType === "nested" || varType === "object" || varType === "objectArray") {
		inputType = varType === "objectArray" ? "objectArray" : "object";
		viewerType = varType === "objectArray" ? "arrayCount" : "objectSummary";
	} else if (inputRaw === "no-input") {
		inputType = "no-input";
		viewerType = "text";
	} else if (inputRaw === "select") {
		inputType = "select";
		viewerType = "status";
	} else if (inputRaw === "checkbox" || varType === "boolean") {
		inputType = "switch";
		viewerType = "badge";
	} else if (inputRaw === "datetime" || varType === "date") {
		inputType = "datetime";
		viewerType = "date";
	} else if (varType === "number" || inputRaw === "number") {
		inputType = "number";
		viewerType = "text";
	} else if (varType === "stringArray") {
		inputType = "text";
		viewerType = "chips";
		isDisplayable = false;
	} else if (inputRaw === "url") {
		inputType = "url";
		viewerType = "link";
	} else if (inputRaw === "email") {
		inputType = "email";
		viewerType = "text";
	} else if (inputRaw === "password") {
		inputType = "password";
		viewerType = "text";
	} else if (inputRaw === "date") {
		inputType = "date";
		viewerType = "date";
	} else if (inputRaw === "time") {
		inputType = "time";
		viewerType = "text";
	} else if (inputRaw === "text-area") {
		inputType = "text-area";
		viewerType = "text";
	} else if (varType === "string" && /(image|img|photo|logo|hero|thumbnail|banner)/.test(a.attributeKey.toLowerCase())) {
		inputType = "url";
		viewerType = "image";
	} else {
		inputType = "text";
		viewerType = "text";
	}

	const enumOpt = opts.find((o) => o.option === "enum");
	const selectOptions = normalizeEnumValue(enumOpt?.value);

	return {
		...a,
		attributeAttributes: (a.attributeAttributes || []).map(mapAttribute),
		inputType,
		viewerType,
		effectiveInputType: inputType,
		effectiveOutputType: viewerType,
		isRequired: constraints.includes("required"),
		isEditable: !constraints.includes("immutable"),
		isCreateable: inputType !== "system-generated" && inputType !== "no-input",
		isDisplayable,
		relatedWith: a.attributeRelation,
		placeholder: "",
		selectOptions,
		columnWidth: 0,
		isSortable: varType === "string" || varType === "number" || varType === "date",
		isVisibleByDefault: isDisplayable && inputType !== "no-input" && inputType !== "system-generated",
		isHidden: false,
		fileKeys,
	};
}

export function mapDefinition(raw: Record<string, unknown>): EntityDefinition {
	const attrs = (raw.entityAttributes as EntityAttribute[]) || [];
	return {
		...(raw as unknown as EntityDefinition),
		mappedAttributes: attrs.map(mapAttribute),
	};
}

export function mergeAttributes(
	def: EntityDefinition,
	config: ToolConfig | null,
): MergedAttribute[] {
	const inputCfgs = config?.inputConfig || [];
	const outputCfgs = config?.outputConfig || [];

	return def.mappedAttributes.map((attr) => {
		const inputCfg = inputCfgs.find((c) => c.attributeKey === attr.attributeKey);
		const outputCfg = outputCfgs.find((c) => c.attributeKey === attr.attributeKey);

		return {
			...attr,
			effectiveInputType: (inputCfg?.inputTypeOverride as InputType) || attr.inputType,
			effectiveOutputType: (outputCfg?.outputType as ViewerType) || attr.viewerType,
			selectOptions: inputCfg?.selectOptions || attr.selectOptions,
			placeholder: inputCfg?.placeholder || attr.placeholder,
			isRequired: inputCfg?.isRequired ?? attr.isRequired,
			isEditable: inputCfg?.isEditable ?? attr.isEditable,
			isVisibleByDefault: outputCfg?.isVisibleByDefault ?? attr.isVisibleByDefault,
			columnWidth: outputCfg?.columnWidth || attr.columnWidth,
			isSortable: outputCfg?.isSortable ?? attr.isSortable,
			isHidden: inputCfg?.isHidden ?? attr.isHidden,
			label: inputCfg?.label ?? attr.label,
			formatOptions: outputCfg?.formatOptions ?? {},
		};
	});
}

export function enumOptions(attr: MergedAttribute): string[] {
	if (attr.selectOptions.length > 0) return attr.selectOptions;
	const enumOpt = (attr.morphedConstraintOptions || []).find((o) => o.option === "enum");
	return normalizeEnumValue(enumOpt?.value);
}

/** Normaliza el value de un constraint enum: admite string[] o string con comas
 *  (morphed-core usa "admin,operator,viewer,agent"; clam usa arrays). */
function normalizeEnumValue(v: unknown): string[] {
	if (Array.isArray(v)) return v.map((x) => String(x));
	if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);
	return [];
}

export function getEditableAttrs(def: EntityDefinition): MergedAttribute[] {
	return def.mappedAttributes.filter(
		(a) => !a.isHidden && a.effectiveInputType !== "system-generated" && a.effectiveInputType !== "no-input" && a.attributeKey !== "id",
	);
}

export function getDisplayAttrs(def: EntityDefinition): MergedAttribute[] {
	return def.mappedAttributes.filter(
		(a) => !a.isHidden && !["deleted", "createdOn", "modifiedOn"].includes(a.attributeKey) && a.attributeKey !== "id" && a.isDisplayable,
	);
}

let _definitions: EntityDefinition[] | null = null;
let _toolConfigs: ToolConfig[] | null = null;

export async function loadEntityDefinitions(force = false): Promise<EntityDefinition[]> {
	if (_definitions && !force) return _definitions;
	try {
		const all = await morphedRead<Record<string, unknown>>("morphed-entity", {
			filters: [{ entitySchema: "clam" }],
			options: { limit: 100 },
			allowPublic: true,
		});
		let defs = all.filter((d) => !(d.entityName as string).includes("Mailer"));
		// morphed-core gestionables desde la sección Morphed (user + organization)
		// Buscar por entityName (no por entitySchema: en prod es "", en dev "morphed-ai")
		try {
			const allDefs = await morphedRead<Record<string, unknown>>("morphed-entity", {
				filters: [],
				options: { limit: 200 },
			});
			defs = defs.concat(
				allDefs.filter((d) => d.entityName === "Morphed User" || d.entityName === "Morphed Organization"),
			);
		} catch { /* zona Morphed no disponible */ }
		_definitions = defs.map(mapDefinition);
		// Registrar name -> endpoint canonico para que resolveEndpoint no slugifique mal
		// nombres con acentos/palabras vacias (ej. Información Privada del Evento).
		registerEntityEndpoints(_definitions.map((d) => [d.entityName, d.entityEndpoint] as [string, string]));
	} catch (e) {
		console.error("[entity-def] loadEntityDefinitions failed:", e);
		_definitions = [];
	}
	return _definitions;
}

export async function loadToolConfigs(force = false): Promise<ToolConfig[]> {
	if (_toolConfigs && !force) return _toolConfigs;
	try {
		_toolConfigs = await morphedRead<ToolConfig>("clam-tool-config", {
			filters: [{ isActive: true }],
			options: { limit: 100 },
		});
	} catch {
		_toolConfigs = [];
	}
	return _toolConfigs;
}
